import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUrl } from 'aws-amplify/storage';
import { useUserContext } from '../../../contexts/UserContext';
import { Card } from '../../ui';
import { IconAngleDown, IconAngleUp } from '@icons';
import {
    buildOrgChartTree,
    countDirectReports,
    countSubtree,
    type OrgChartNode,
} from '../../../utils/orgChartHierarchy';
import { Role } from '../../../utils/permissions';
import '../../../css/UserProfileTabs.css';

type OrgChartTabProps = {
    focusUserId?: string | null;
};

/** Survives remounts and page refresh until the user expands again. */
const COLLAPSE_STORAGE_PREFIX = 'eif.orgChart.collapsed:';

const companyCollapseKey = (companyId?: string | null) => companyId?.trim() || '_';

const storageKeyForCompany = (companyId?: string | null) =>
    `${COLLAPSE_STORAGE_PREFIX}${companyCollapseKey(companyId)}`;

const readCollapsedIds = (companyId?: string | null): Set<string> => {
    if (typeof window === 'undefined') return new Set();
    try {
        const raw = window.localStorage.getItem(storageKeyForCompany(companyId));
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return new Set();
        return new Set(parsed.filter((id): id is string => typeof id === 'string' && id.length > 0));
    } catch {
        return new Set();
    }
};

const writeCollapsedIds = (companyId: string | null | undefined, ids: Set<string>) => {
    if (typeof window === 'undefined') return;
    try {
        const key = storageKeyForCompany(companyId);
        if (ids.size === 0) {
            window.localStorage.removeItem(key);
            return;
        }
        window.localStorage.setItem(key, JSON.stringify([...ids]));
    } catch {
        // Ignore quota / private-mode failures; in-memory state still works for the session.
    }
};

const getInitials = (name?: string | null): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0][0] || '?').toUpperCase();
};

const roleAccentClass = (role: Role | null): string => {
    switch (role) {
        case Role.SUPER_ADMIN:
            return 'is-super-admin';
        case Role.ADMIN:
            return 'is-admin';
        case Role.FOREMAN:
            return 'is-foreman';
        case Role.INSPECTOR:
            return 'is-inspector';
        case Role.SERVICE_TECHNICIAN:
            return 'is-service-tech';
        default:
            return 'is-unknown';
    }
};

const OrgChartPersonCard = ({
    node,
    photoUrl,
    collapsed,
    onToggle,
    onOpen,
}: {
    node: OrgChartNode;
    photoUrl?: string;
    collapsed: boolean;
    onToggle: () => void;
    onOpen: () => void;
}) => {
    const direct = countDirectReports(node);
    const team = countSubtree(node);
    const hasChildren = node.children.length > 0;

    return (
        <Card
            variant="card-sm"
            className={`upt-org-card ${roleAccentClass(node.role)} ${node.isFocus ? 'is-focus' : ''}`}
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen();
                }
            }}
        >
            <div className="upt-org-avatar" aria-hidden="true">
                {photoUrl ? (
                    <img
                        src={photoUrl}
                        alt=""
                        className="upt-org-avatar-img"
                        onError={(event) => {
                            event.currentTarget.style.display = 'none';
                        }}
                    />
                ) : (
                    <span className="upt-org-avatar-initials">{getInitials(node.name)}</span>
                )}
            </div>
            <div className="upt-org-card-name">{node.name}</div>
            <div className="upt-org-card-role">{node.roleLabel}</div>
            {node.position ? <div className="upt-org-card-position">{node.position}</div> : null}
            <div className="upt-org-card-meta">
                <span title="Team size under this person">{team}</span>
                <span title="Direct reports">{direct}</span>
                {hasChildren ? (
                    <button
                        type="button"
                        className="upt-org-collapse"
                        aria-label={collapsed ? 'Expand reports' : 'Collapse reports'}
                        aria-expanded={!collapsed}
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggle();
                        }}
                    >
                        {collapsed ? (
                            <IconAngleDown iconClasses="upt-org-collapse-icon" />
                        ) : (
                            <IconAngleUp iconClasses="upt-org-collapse-icon" />
                        )}
                    </button>
                ) : (
                    <span className="upt-org-collapse-spacer" aria-hidden="true" />
                )}
            </div>
        </Card>
    );
};

const OrgChartBranch = ({
    nodes,
    photoUrls,
    collapsedIds,
    onToggle,
    onOpen,
}: {
    nodes: OrgChartNode[];
    photoUrls: Record<string, string>;
    collapsedIds: Set<string>;
    onToggle: (id: string) => void;
    onOpen: (id: string) => void;
}) => {
    if (nodes.length === 0) return null;

    return (
        <ul className="upt-org-level">
            {nodes.map((node) => {
                const collapsed = collapsedIds.has(node.id);
                const showChildren = node.children.length > 0 && !collapsed;
                return (
                    <li key={node.id} className="upt-org-node">
                        <OrgChartPersonCard
                            node={node}
                            photoUrl={photoUrls[node.id]}
                            collapsed={collapsed}
                            onToggle={() => onToggle(node.id)}
                            onOpen={() => onOpen(node.id)}
                        />
                        {showChildren ? (
                            <OrgChartBranch
                                nodes={node.children}
                                photoUrls={photoUrls}
                                collapsedIds={collapsedIds}
                                onToggle={onToggle}
                                onOpen={onOpen}
                            />
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );
};

export const OrgChartTab = ({ focusUserId }: OrgChartTabProps) => {
    const navigate = useNavigate();
    const { allCompanyUsers, company } = useUserContext();
    const companyId = company?.id;
    const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => readCollapsedIds(companyId));
    const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});

    const { roots, counts } = useMemo(
        () =>
            buildOrgChartTree(allCompanyUsers, {
                companyOwnerId: company?.companyOwnerId,
                focusUserId,
            }),
        [allCompanyUsers, company?.companyOwnerId, focusUserId],
    );

    useEffect(() => {
        setCollapsedIds(readCollapsedIds(companyId));
    }, [companyId]);

    useEffect(() => {
        let isCancelled = false;
        const peopleWithPhotos = allCompanyUsers.filter((user) => user.id && user.userPhoto);

        (async () => {
            const updates: Record<string, string> = {};
            await Promise.all(
                peopleWithPhotos.map(async (user) => {
                    try {
                        const result = await getUrl({
                            path: user.userPhoto as string,
                            options: { expiresIn: 3600 },
                        });
                        updates[user.id] = result.url.href;
                    } catch {
                        // Initials fallback
                    }
                }),
            );
            if (!isCancelled) {
                setPhotoUrls(updates);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [allCompanyUsers]);

    const toggleCollapsed = (id: string) => {
        setCollapsedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            writeCollapsedIds(companyId, next);
            return next;
        });
    };

    return (
        <div className="upt-org-tab">
            <div className="upt-org-header">
                <div>
                    <h3 className="upt-section-title">Organization Chart</h3>
                    <p className="upt-org-subtitle">
                        Hierarchy from company roles (Super Admin → Admin → Foreman → Field staff).
                        Supervisor names on Employment are used when they uniquely match a teammate.
                    </p>
                </div>
                <div className="upt-org-counts" aria-label="Company role counts">
                    <span className="upt-org-count-pill">{counts.total} people</span>
                    <span className="upt-org-count-pill">{counts.superAdmin} super admin</span>
                    <span className="upt-org-count-pill">{counts.admin} admin</span>
                    <span className="upt-org-count-pill">{counts.foreman} foreman</span>
                    <span className="upt-org-count-pill">
                        {counts.inspector + counts.serviceTechnician} field
                    </span>
                </div>
            </div>

            {roots.length === 0 ? (
                <div className="upt-org-empty">No company users to display.</div>
            ) : (
                <div className="upt-org-scroll">
                    <div className="upt-org-tree">
                        <OrgChartBranch
                            nodes={roots}
                            photoUrls={photoUrls}
                            collapsedIds={collapsedIds}
                            onToggle={toggleCollapsed}
                            onOpen={(id) => navigate(`/user/${id}?tab=org-chart`)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
